/**
 * Geocode an address to lat/lon using free providers (ArcGIS, Photon, Nominatim).
 */
async function geocodeAddress(address: string): Promise<{ lat: number; lon: number } | null> {
  if (!address?.trim()) return null;

  const tryArcGis = async (): Promise<{ lat: number; lon: number } | null> => {
    const endpoint = `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?f=pjson&maxLocations=1&outFields=Match_addr&sourceCountry=ROU&SingleLine=${encodeURIComponent(address)}`;
    const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    const first = data?.candidates?.[0];
    const lat = Number(first?.location?.y);
    const lon = Number(first?.location?.x);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon };
  };

  const tryPhoton = async (): Promise<{ lat: number; lon: number } | null> => {
    const endpoint = `https://photon.komoot.io/api/?q=${encodeURIComponent(address)}&limit=1`;
    const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    const first = data?.features?.[0];
    const lon = Number(first?.geometry?.coordinates?.[0]);
    const lat = Number(first?.geometry?.coordinates?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon };
  };

  const tryNominatim = async (): Promise<{ lat: number; lon: number } | null> => {
    const endpoint = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=ro&q=${encodeURIComponent(address)}`;
    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json', 'Accept-Language': 'ro' },
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!Array.isArray(data) || !data.length) return null;
    const lat = Number(data[0]?.lat);
    const lon = Number(data[0]?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon };
  };

  for (const fn of [tryArcGis, tryPhoton, tryNominatim]) {
    try {
      const coords = await fn();
      if (coords) return coords;
    } catch {
      // continue to next provider
    }
  }
  return null;
}

export type GetDistanceOptions = {
  /** If true (default), returns round-trip distance and time (office → destination → office). */
  roundTrip?: boolean;
};

/**
 * Get driving distance (km) and duration (minutes) from the office to a destination address.
 * Office: Strada Tiglarilor, nr 17, Oradea.
 * Uses OSRM public routing API (driving).
 * @param roundTrip - true (default) for dus-intors; false for one-way (office → destination only).
 */
export async function getDistanceAndDurationFromOffice(
  destinationAddress: string,
  options?: GetDistanceOptions
): Promise<{ distanceKm: number; durationMinutes: number } | null> {
  const roundTrip = options?.roundTrip !== false;
  const OFFICE_ADDRESS = 'Strada Tiglarilor, nr 17, Oradea, Romania';

  const [officeCoords, destCoords] = await Promise.all([
    geocodeAddress(OFFICE_ADDRESS),
    geocodeAddress(destinationAddress),
  ]);

  if (!officeCoords || !destCoords) return null;

  // OSRM: coordinates as lon,lat
  const coords = `${officeCoords.lon},${officeCoords.lat};${destCoords.lon},${destCoords.lat}`;
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=false`;

  try {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    if (data?.code !== 'Ok' || !data?.routes?.[0]) return null;
    const route = data.routes[0];
    const distanceMeters = Number(route.distance);
    const durationSeconds = Number(route.duration);
    if (!Number.isFinite(distanceMeters) || !Number.isFinite(durationSeconds)) return null;
    const multiplier = roundTrip ? 2 : 1;
    const distanceKm = Math.round((distanceMeters / 1000) * multiplier * 10) / 10;
    const durationMinutes = Math.round((durationSeconds / 60) * multiplier);
    return { distanceKm: Math.max(0, distanceKm), durationMinutes: Math.max(0, durationMinutes) };
  } catch {
    return null;
  }
}

/**
 * Get driving distance (km) and duration (minutes) from a given origin (lat/lon) to a destination address.
 * Uses OSRM public routing API (driving). One-way only.
 */
export async function getDistanceAndDurationFromCoordsToAddress(
  originLat: number,
  originLon: number,
  destinationAddress: string
): Promise<{ distanceKm: number; durationMinutes: number } | null> {
  if (!destinationAddress?.trim()) return null;
  if (!Number.isFinite(originLat) || !Number.isFinite(originLon)) return null;

  const destCoords = await geocodeAddress(destinationAddress);
  if (!destCoords) return null;

  // OSRM: coordinates as lon,lat
  const coords = `${originLon},${originLat};${destCoords.lon},${destCoords.lat}`;
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=false`;

  try {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    if (data?.code !== 'Ok' || !data?.routes?.[0]) return null;
    const route = data.routes[0];
    const distanceMeters = Number(route.distance);
    const durationSeconds = Number(route.duration);
    if (!Number.isFinite(distanceMeters) || !Number.isFinite(durationSeconds)) return null;
    const distanceKm = Math.round((distanceMeters / 1000) * 10) / 10;
    const durationMinutes = Math.round(durationSeconds / 60);
    return { distanceKm: Math.max(0, distanceKm), durationMinutes: Math.max(0, durationMinutes) };
  } catch {
    return null;
  }
}
